import React from 'react';
import { C, FONTE_MONO } from '../paleta';
import { rotuloComCodigo, temCodigoDistinto } from '../utils/codigoEmpresa';

/**
 * Componente ÚNICO de exibição "nome + código" da empresa (pedido 08/09) — TODO
 * ponto da página que mostra empresa com código passa por aqui, nunca por
 * formatação inline duplicada. Duas variantes:
 * - "sufixo" (card Fila, 3 níveis): código à direita, colado ao nome com underscore
 *   ("NomeEmpresa_1117"), em mono menor e cor secundária;
 * - "bloco" (popups dos nós e coluna Empresa do Resumo): código ABAIXO do nome, à
 *   esquerda, linha apertada, fonte menor/mono, cor secundária da paleta.
 *
 * Fallbacks (via utils/codigoEmpresa): sem nome → só código; sem código → só nome;
 * nome igual ao código (fallback do `nomeEmpresa()`) → só o código, sem duplicar.
 * O tooltip preserva SEMPRE o conjunto ("NomeEmpresa_1117").
 *
 * `style` é o estilo de LAYOUT/tipografia do ponto de uso (flex, fontSize, peso) —
 * o componente só é dono da relação visual entre nome e código.
 */

interface NomeEmpresaProps {
  nome: string | null;
  codigo: string | null;
  variante: 'sufixo' | 'bloco';
  style?: React.CSSProperties;
  /**
   * Rótulo ABAIXO do código — só na variante "bloco" (pedido 08/09: a linha da
   * empresa fora das regras exibe "inativa" ou "fora de vigência" sob o código —
   * `rotuloForaDasRegras` decide o texto). Exibido como TARJA vermelha (pedido
   * 08/09, 2ª rodada): fundo no vermelho de erro da paleta — o mesmo das pílulas
   * de Falha — com escrita branca, para saltar aos olhos (o âmbar discreto
   * passava despercebido).
   */
  rotuloAlerta?: string | null;
}

const codigoStyleSufixo: React.CSSProperties = {
  fontFamily: FONTE_MONO,
  fontSize: '0.85em',
  fontWeight: 400,
  color: 'oklch(0.55 0.015 165)',
};

const codigoStyleBloco: React.CSSProperties = {
  fontFamily: FONTE_MONO,
  fontSize: '10px',
  lineHeight: 1.1,
  fontWeight: 400,
  color: 'oklch(0.55 0.015 165)',
};

/**
 * "inativa"/"fora de vigência" sob o código — TARJA vermelha (pedido 08/09): fundo
 * no vermelho de erro da paleta (o mesmo `C.red` das pílulas de Falha) com escrita
 * branca, uppercase, mono pequeno e padding fino — badge, não mais texto âmbar.
 */
const rotuloAlertaStyle: React.CSSProperties = {
  display: 'inline-block',
  fontFamily: FONTE_MONO,
  fontSize: '9.5px',
  lineHeight: 1.4,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: C.white,
  background: C.red,
  padding: '1px 6px',
  borderRadius: '4px',
};

export const NomeEmpresa: React.FC<NomeEmpresaProps> = ({
  nome,
  codigo,
  variante,
  style,
  rotuloAlerta,
}) => {
  const rotulo = rotuloComCodigo(nome, codigo);
  const alerta =
    variante === 'bloco' && rotuloAlerta ? (
      <span style={rotuloAlertaStyle}>{rotuloAlerta}</span>
    ) : null;

  // Só uma das partes disponível (ou nome = código): exibe a parte que há, no
  // estilo do ponto de uso — nada de "_" solto nem código duplicado. No bloco,
  // o rótulo de alerta ainda entra abaixo (a inativa sem cadastro segue marcada).
  if (!temCodigoDistinto(nome, codigo)) {
    if (alerta) {
      return (
        <span
          title={rotulo}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '1px',
            minWidth: 0,
            ...style,
          }}
        >
          <span>{rotulo || '—'}</span>
          {alerta}
        </span>
      );
    }
    return (
      <span title={rotulo} style={style}>
        {rotulo || '—'}
      </span>
    );
  }

  const n = (nome ?? '').trim();
  const c = (codigo ?? '').trim();

  if (variante === 'sufixo') {
    return (
      <span title={rotulo} style={style}>
        {n}
        <span style={codigoStyleSufixo}>_{c}</span>
      </span>
    );
  }

  return (
    <span
      title={rotulo}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '1px',
        minWidth: 0,
        ...style,
      }}
    >
      <span
        style={{
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {n}
      </span>
      <span style={codigoStyleBloco}>{c}</span>
      {alerta}
    </span>
  );
};
